export const project = [
    {
        "title":"AI-Powered Talent Acquisition Platform",
        "description": "Delivered a cost-effective, serverless recruitment platform that reduced hiring time by 60% and processing costs by 75%. The solution leverages AI-powered resume screening to automatically identify top candidates, enabling recruiters to focus on high-value activities. Built on AWS serverless architecture, the platform scales automatically with zero infrastructure management overhead.",
        "image": require('./images/serverless.png'),
        "highlites": [
          "60% reduction in time-to-hire through automated candidate screening and intelligent matching",
          "75% cost savings by eliminating traditional infrastructure and reducing manual processing overhead",
          "Seamless integration with Slack for real-time notifications and streamlined team collaboration",
          "AI-powered NLP processing automatically extracts and analyzes candidate qualifications",
          "Enterprise-grade security with automated authentication and secure API endpoints",
          "Zero infrastructure management with fully serverless architecture, reducing operational burden"
        ],
        "architecture": {
          "pattern": "Serverless Microservices Architecture",
          "region": "us-west-2 (Oregon)",
          "availabilityZones": ["us-west-2a", "us-west-2b"],
          "components": [
            {
              "service": "Amazon API Gateway",
              "purpose": "Single entry point for all client requests with RESTful API design, authentication integration, and rate limiting"
            },
            {
              "service": "AWS Lambda Functions",
              "purpose": "Serverless compute for application logic including ats-lambda (Slack integration), db-access-ui (UI database operations), db-access (core database operations), and resume-nlp (AI-powered resume processing)"
            },
            {
              "service": "AWS Cognito",
              "purpose": "User authentication and identity management with federated authentication (Google, LinkedIn), MFA support, and JWT token-based authorization"
            },
            {
              "service": "Amazon RDS PostgreSQL",
              "purpose": "Managed relational database with multi-AZ deployment, automated backups, encryption at rest, and high availability"
            },
            {
              "service": "Amazon S3",
              "purpose": "Object storage for resumes, job postings, and static React.js application assets with versioning, lifecycle policies, and server-side encryption"
            },
            {
              "service": "Amazon VPC",
              "purpose": "Network isolation with private subnets for Lambda functions and RDS, security groups for traffic control, and multi-AZ deployment"
            },
            {
              "service": "NAT Gateway",
              "purpose": "Provides outbound internet access for private subnet resources, enabling S3 uploads, external API calls, and Slack integration"
            },
            {
              "service": "Slack Bot (Containerized)",
              "purpose": "Containerized application deployed within VPC for Slack integration, real-time notifications, and team collaboration"
            },
            {
              "service": "Amazon ECR",
              "purpose": "Docker container image storage for Slack bot with versioned image management and security scanning"
            },
            {
              "service": "Amazon CloudFront",
              "purpose": "Content delivery network for static React.js assets with global distribution, reduced latency, and DDoS protection"
            }
          ],
          "dataFlow": [
            "User authentication flow through Cognito with federated identity providers",
            "Job posting workflow with S3 storage and Slack notifications",
            "Resume upload and AI-powered NLP processing pipeline",
            "Database query operations through secure VPC endpoints",
            "Real-time Slack integration for notifications and collaboration"
          ],
          "security": [
            "VPC isolation with private subnets for sensitive resources",
            "Cognito-based authentication with JWT tokens",
            "API Gateway authorizers and request validation",
            "RDS encryption at rest and TLS encryption in transit",
            "S3 server-side encryption (SSE-S3 or SSE-KMS)",
            "IAM roles with least privilege access",
            "Security groups and network ACLs for traffic control"
          ],
          "scalability": [
            "Automatic Lambda scaling to handle varying workloads",
            "RDS read replicas for read scaling",
            "S3 unlimited storage capacity",
            "API Gateway caching for improved performance",
            "CloudFront CDN for global content delivery"
          ]
        }
        },
        {
        "title":"Global Multi-Region Cloud Infrastructure",
        "description": "Designed and delivered a production-ready, multi-region cloud infrastructure in under 10 weeks, enabling global business expansion with 99.9% uptime SLA. The solution spans three continents (US East, US West, Europe) with automated failover capabilities, reducing infrastructure costs by 40% through intelligent resource optimization and spot instance utilization.",
        "image": require('./images/AWS_Multi_Region_Network.png'),
        "highlites": [
          "10-week delivery timeline, 50% faster than industry standard for multi-region deployments",
          "40% infrastructure cost reduction through automated scaling and spot instance optimization",
          "99.9% uptime SLA with automated disaster recovery and multi-region redundancy",
          "Zero-downtime deployments enabled through automated CI/CD pipelines and blue-green deployments",
          "Enterprise security compliance with centralized secret management and role-based access controls",
          "Infrastructure as Code approach ensures consistent, repeatable deployments across all environments"
        ],
        "architecture": {
          "pattern": "Multi-Region Hub-and-Spoke Network Architecture",
          "regions": ["us-east-1 (N. Virginia)", "us-west-2 (Oregon)", "eu-central-1 (Frankfurt)"],
          "availabilityZones": ["Multiple AZs per region for high availability"],
          "components": [
            {
              "service": "AWS Transit Gateway",
              "purpose": "Centralized network hub connecting multiple VPCs across regions and accounts. Manages Layer 3 routing with dedicated route tables for account and region-specific traffic control, eliminating the need for VPC peering"
            },
            {
              "service": "Transit Gateway Peering",
              "purpose": "Cross-region connectivity between Transit Gateways in us-east-1, us-west-2, and eu-central-1, enabling seamless inter-region communication with controlled routing"
            },
            {
              "service": "Transit Gateway Route Tables",
              "purpose": "Dedicated route tables for each account and region, providing granular traffic control and network segmentation. Enables policy-based routing and traffic isolation"
            },
            {
              "service": "Amazon EKS (Elastic Kubernetes Service)",
              "purpose": "Managed Kubernetes clusters deployed across all three regions with configurable spot and on-demand autoscaling groups. Isolated clusters for dev, QA, and production environments"
            },
            {
              "service": "HashiCorp Vault",
              "purpose": "Centralized secret management with Kubernetes authentication enabled. Provides pod and namespace-level access controls for secure credential management across all environments"
            },
            {
              "service": "Terraform Cloud",
              "purpose": "Infrastructure as Code platform managing all AWS resources. Multiple Git repositories with version-controlled modules, git tags for module versioning, and dedicated repos for core networking resources"
            },
            {
              "service": "Terraform Workspaces",
              "purpose": "Environment isolation using workspaces to reuse infrastructure code across dev, QA, and production workloads. Enables consistent deployments with environment-specific configurations"
            },
            {
              "service": "CI/CD Pipeline Framework",
              "purpose": "Automated build and release pipeline for faster feature delivery. Integrates with Git repositories, Terraform, and Kubernetes for end-to-end automation"
            },
            {
              "service": "AWS Auto Scaling Groups",
              "purpose": "Configurable autoscaling with spot and on-demand instance types for cost optimization. Automatic scaling based on workload demands across all regions"
            },
            {
              "service": "Logging & Monitoring",
              "purpose": "Comprehensive observability stack with centralized logging and monitoring for better visibility, quick learning cycles, and proactive issue detection across all regions"
            },
            {
              "service": "Amazon VPC",
              "purpose": "Isolated network environments in each region with private and public subnets, security groups, and network ACLs for secure resource deployment"
            },
            {
              "service": "Git Version Control",
              "purpose": "Multiple Git repositories for infrastructure code management. Dedicated repository for core resources (Transit Gateways, Peering Attachments, Route Tables) with git tags for module versioning"
            }
          ],
          "dataFlow": [
            "Inter-region traffic routing through Transit Gateway peering connections",
            "Account and region-specific traffic control via dedicated Transit Gateway route tables",
            "Application deployments to EKS clusters across multiple regions with blue-green deployment strategy",
            "Secret retrieval from HashiCorp Vault with Kubernetes pod-level authentication",
            "Infrastructure provisioning through Terraform Cloud with workspace-based environment isolation",
            "Automated CI/CD pipeline execution for application and infrastructure updates"
          ],
          "security": [
            "Centralized secret management with HashiCorp Vault and Kubernetes authentication",
            "Pod and namespace-level access controls for fine-grained security",
            "VPC isolation with private subnets for sensitive workloads",
            "Transit Gateway route tables for network segmentation and traffic isolation",
            "IAM roles and policies for least privilege access across all regions",
            "Encrypted transit between regions via Transit Gateway peering",
            "Security groups and network ACLs for traffic control at VPC level",
            "Git-based version control with audit trail for all infrastructure changes"
          ],
          "scalability": [
            "Multi-region deployment across three continents for global scalability",
            "EKS clusters with configurable spot and on-demand autoscaling groups",
            "Auto Scaling Groups automatically adjust capacity based on workload demands",
            "Transit Gateway handles high-throughput inter-region and inter-VPC communication",
            "Terraform workspaces enable rapid environment provisioning and scaling",
            "Spot instance utilization reduces costs by up to 90% for fault-tolerant workloads",
            "Horizontal pod autoscaling in EKS for application-level scaling"
          ],
          "infrastructureManagement": [
            "Infrastructure as Code with Terraform Cloud for version-controlled infrastructure",
            "Multiple Git repositories with dedicated repos for core networking resources",
            "Git tags for module versioning and release management",
            "Terraform workspaces for environment isolation (dev, QA, production)",
            "Automated CI/CD pipelines for infrastructure and application deployments",
            "Centralized state management in S3 backend with encryption"
          ]
        }
        },
        {
          "title":"Enterprise Cloud Governance & Automation Platform",
          "description": "Transformed cloud operations with a comprehensive governance platform that reduced deployment time by 70% and improved security compliance by 50%. The solution provides centralized control, automated policy enforcement, and seamless single sign-on integration, enabling faster innovation while maintaining enterprise security standards.",
          "highlites": [
            "70% reduction in deployment time through automated infrastructure provisioning and governance automation",
            "50% improvement in security compliance with automated policy enforcement and guardrails",
            "Unified access management with single sign-on integration, reducing access management overhead by 80%",
            "Automated governance controls ensure consistent security and compliance across all cloud resources",
            "Faster time-to-market for new applications with self-service infrastructure provisioning",
            "Complete audit trail and observability for all cloud operations and resource changes"
          ],
          "architecture": {
            "pattern": "Centralized Governance & Multi-Account Architecture",
            "region": "Multi-region deployment",
            "availabilityZones": ["Multiple AZs per region"],
            "components": [
              {
                "service": "AWS Control Tower",
                "purpose": "Centralized governance platform providing automated account provisioning, guardrails for compliance, and landing zone best practices. Manages multi-account AWS environment with pre-configured security and compliance controls"
              },
              {
                "service": "AWS Single Sign-On (SSO)",
                "purpose": "Centralized identity and access management with external SSO integration via Azure AD. Enables federated authentication, single sign-on to AWS accounts and applications, and centralized user management. Reduces access management overhead by 80%"
              },
              {
                "service": "AWS Organizations",
                "purpose": "Multi-account management with Organizational Units (OUs) for logical grouping of accounts. Enables centralized billing, consolidated logging, and policy-based account management across the entire organization"
              },
              {
                "service": "Service Control Policies (SCPs)",
                "purpose": "Centralized policy-based access control at the organization level. SCPs define maximum permissions for accounts and OUs, preventing unauthorized actions and ensuring compliance. Applied hierarchically to enforce governance across all accounts"
              },
              {
                "service": "Control Tower Guardrails",
                "purpose": "Automated compliance checks and preventive controls. Mandatory and strongly recommended guardrails enforce security best practices, prevent non-compliant configurations, and provide continuous compliance monitoring across all accounts"
              },
              {
                "service": "Amazon EKS (Elastic Kubernetes Service)",
                "purpose": "Managed Kubernetes clusters with SSO-based authentication. EKS clusters configured with RBAC integration for SSO users and groups, enabling fine-grained access control at pod and namespace levels"
              },
              {
                "service": "EKS RBAC Integration",
                "purpose": "Role-Based Access Control integration with AWS SSO. Enables SSO users and groups to authenticate to EKS clusters with Kubernetes RBAC policies, providing seamless access management from SSO to Kubernetes resources"
              },
              {
                "service": "Azure AD Integration",
                "purpose": "External identity provider integration with AWS SSO. Enables federated authentication using existing Azure AD credentials, eliminating the need for separate AWS IAM user management"
              },
              {
                "service": "Fluentd",
                "purpose": "Centralized logging and log aggregation across all accounts and services. Collects, processes, and forwards logs to centralized storage for monitoring, compliance, and troubleshooting"
              },
              {
                "service": "AWS CloudTrail",
                "purpose": "Comprehensive audit logging of all API calls and user activities across all accounts. Provides complete audit trail for compliance, security analysis, and operational troubleshooting"
              },
              {
                "service": "AWS Config",
                "purpose": "Configuration compliance monitoring and resource inventory. Continuously monitors and records AWS resource configurations, enabling compliance auditing and change tracking"
              }
            ],
            "dataFlow": [
              "User authentication through Azure AD with federated SSO to AWS",
              "SSO-based access to AWS accounts and services with centralized permission management",
              "Service Control Policies applied at organization level to enforce governance",
              "Control Tower guardrails continuously monitor and prevent non-compliant configurations",
              "EKS cluster access via SSO authentication with Kubernetes RBAC authorization",
              "Centralized logging through Fluentd for observability across all accounts"
            ],
            "security": [
              "Centralized identity management with AWS SSO and Azure AD federation",
              "Service Control Policies enforce maximum permissions and prevent unauthorized actions",
              "Control Tower guardrails provide automated compliance checks and preventive controls",
              "EKS RBAC integration with SSO for fine-grained Kubernetes access control",
              "Multi-account isolation with Organizational Units for logical separation",
              "CloudTrail provides comprehensive audit logging of all API calls and activities",
              "AWS Config enables continuous compliance monitoring and configuration tracking",
              "Centralized logging with Fluentd for security event analysis and forensics",
              "Guardrails prevent non-compliant resource configurations automatically",
              "Role-based access control at both AWS and Kubernetes levels"
            ],
            "scalability": [
              "Multi-account architecture scales to support unlimited AWS accounts",
              "Organizational Units enable logical grouping and policy application at scale",
              "SSO integration supports thousands of users with centralized management",
              "EKS clusters scale horizontally with SSO-based access control",
              "Automated account provisioning through Control Tower for rapid scaling",
              "Centralized logging and monitoring scales with organizational growth"
            ],
            "governance": [
              "AWS Control Tower provides centralized governance and compliance framework",
              "Service Control Policies define and enforce organization-wide security policies",
              "Control Tower guardrails ensure consistent compliance across all accounts",
              "Organizational Units enable hierarchical policy application and account management",
              "Automated account provisioning with pre-configured security and compliance controls",
              "Centralized audit trail through CloudTrail and AWS Config for compliance reporting",
              "Self-service infrastructure provisioning with governance guardrails",
              "Policy-based access control prevents unauthorized resource creation and modifications"
            ]
          }
        },
]